import { useAuth0 } from '@auth0/auth0-react';
import { useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useReducer, useState } from 'react';

import { ApprovalRequestState } from '../../../types/ApprovalRequestState';
import { useToasts } from '../../cdl/Toast/useToasts';
import { APPROVAL_STATE } from '../../common/approvalState.constant';
import { safeDecryptApprovalToken } from '../../common/crypto/tokenDecryption';
import { translate } from '../../common/helpers/translate.helper';
import { Box } from '../../common/ui/Box';
import { LoadingWithWobble } from '../../LoadingWithWobble';

import { ERRORS } from './errors.constants';
import {
    getOfferAndApprovalRequestsById,
    updateApprovalRequest as updateApprovalRequestApi,
} from './offer-approval-request.api';
import { OfferApprovalRequestContent } from './OfferApprovalRequestContent';

interface State {
    offer: any | null;
    approvalRequests: any[];
    fetchingData: boolean;
    error: string | null;
    updatingApprovalRequestState?: ApprovalRequestState | null;
}

interface Action {
    type: string;
    offers?: any[];
    approvalRequests?: any[];
    state?: ApprovalRequestState;
    updatedApprovalRequest?: any;
}

const TYPES = {
    GET_PENDING: 'GET_PENDING',
    GET_SUCCESS: 'GET_SUCCESS',
    GET_ERROR_GONE: 'GET_ERROR_GONE',
    GET_ERROR_UNAUTHORIZED: 'GET_ERROR_UNAUTHORIZED',
    GET_ERROR: 'GET_ERROR',

    UPDATE_APPROVAL_REQUEST_PENDING: 'UPDATE_APPROVAL_REQUEST_PENDING',
    UPDATE_APPROVAL_REQUEST_SUCCESS: 'UPDATE_APPROVAL_REQUEST_SUCCESS',
    UPDATE_APPROVAL_REQUEST_ERROR: 'UPDATE_APPROVAL_REQUEST_ERROR',
};

const initialState: State = {
    offer: null,
    approvalRequests: [],
    fetchingData: true,
    error: null,
};

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case TYPES.GET_PENDING: {
            return initialState;
        }
        case TYPES.GET_SUCCESS: {
            return {
                offer: action.offers?.[0] || null,
                approvalRequests: action.approvalRequests || [],
                fetchingData: false,
                error: null,
            };
        }
        case TYPES.GET_ERROR_GONE: {
            return {
                offer: initialState.offer,
                approvalRequests: initialState.approvalRequests,
                fetchingData: false,
                error: ERRORS.GONE,
            };
        }
        case TYPES.GET_ERROR_UNAUTHORIZED: {
            return {
                offer: initialState.offer,
                approvalRequests: initialState.approvalRequests,
                fetchingData: false,
                error: ERRORS.UNAUTHORIZED,
            };
        }
        case TYPES.GET_ERROR: {
            return {
                offer: initialState.offer,
                approvalRequests: initialState.approvalRequests,
                fetchingData: false,
                error: ERRORS.DEFAULT,
            };
        }
        case TYPES.UPDATE_APPROVAL_REQUEST_PENDING: {
            return {
                ...state,
                updatingApprovalRequestState: action.state,
            };
        }
        case TYPES.UPDATE_APPROVAL_REQUEST_SUCCESS: {
            state.approvalRequests[0] = {
                ...state.approvalRequests[0],
                ...action.updatedApprovalRequest,
            };

            return {
                ...state,
                approvalRequests: state.approvalRequests,
                updatingApprovalRequestState: null,
            };
        }
        case TYPES.UPDATE_APPROVAL_REQUEST_ERROR: {
            return {
                ...state,
                updatingApprovalRequestState: null,
            };
        }
        default:
            throw new Error();
    }
};

export const OfferApprovalRequest = () => {
    const { offerId } = useParams({ from: '/offer/$offerId/approval-request' });
    const { token, secret } = useSearch({
        from: '/offer/$offerId/approval-request',
        select: (search: any) => ({
            token: search.token as string | undefined,
            secret: search.secret as string | undefined,
        }),
    });
    const { addToast, addErrorToast } = useToasts();

    const auth0 = useAuth0();
    const [toolToken, setToolToken] = useState<string | null>(null);
    const [isLoadingToolToken, setIsLoadingToolToken] = useState<boolean>(true);

    const [state, dispatch] = useReducer(reducer, initialState);
    const [decryptedToken, setDecryptedToken] = useState<string | null>(null);
    const [isDecrypting, setIsDecrypting] = useState<boolean>(false);

    // Handle token decryption
    useEffect(() => {
        if (secret?.trim() && !decryptedToken && !isDecrypting) {
            setIsDecrypting(true);

            // Attempt to decrypt the secret
            const decrypted = safeDecryptApprovalToken(secret);

            if (decrypted) {
                setDecryptedToken(decrypted);
            }

            setIsDecrypting(false);
        }
    }, [secret, decryptedToken, isDecrypting]);

    useEffect(() => {
        const getToolToken = async () => {
            try {
                const token = await auth0.getAccessTokenSilently();
                setToolToken(token);
            } catch {
                setToolToken(null);
            } finally {
                setIsLoadingToolToken(false);
            }
        };

        getToolToken();
    }, [auth0]);

    // Determine which token to use for API calls
    const effectiveToken = decryptedToken || token || toolToken;

    useEffect(() => {
        if (!effectiveToken) {
            return;
        }
        dispatch({
            type: TYPES.GET_PENDING,
        });

        getOfferAndApprovalRequestsById({ offerId, token: effectiveToken })
            .then((response) => {
                dispatch({
                    type: TYPES.GET_SUCCESS,
                    offers: response.offers,
                    approvalRequests: response.approvalRequests,
                });
            })
            .catch((error) => {
                if (!error.response) {
                    dispatch({ type: TYPES.GET_ERROR });
                    return;
                }

                if (error.response.status === 410) {
                    dispatch({ type: TYPES.GET_ERROR_GONE });
                    return;
                }

                if (error.response.status === 403) {
                    dispatch({ type: TYPES.GET_ERROR_UNAUTHORIZED });
                    return;
                }

                dispatch({ type: TYPES.GET_ERROR });
            });
    }, [offerId, effectiveToken]);

    useEffect(() => {
        if (state.offer) {
            document.title = translate('approvalRequest.pageTitle', {
                vesselName: state.offer.vessel.name,
                portName: state.offer.port.name,
            });
        }
    }, [state.offer]);

    const updateApprovalRequest = ({ approvalRequest }: { approvalRequest: any }) => {
        if (state.updatingApprovalRequestState || !effectiveToken) {
            return;
        }

        dispatch({
            type: TYPES.UPDATE_APPROVAL_REQUEST_PENDING,
            state: approvalRequest.state,
        });

        updateApprovalRequestApi({
            approvalRequestId: approvalRequest.id,
            approvalRequest,
            token: effectiveToken,
        })
            .then((updatedApprovalRequest) => {
                dispatch({
                    type: TYPES.UPDATE_APPROVAL_REQUEST_SUCCESS,
                    updatedApprovalRequest,
                });

                if (approvalRequest.state === APPROVAL_STATE.DECLINED) {
                    addToast(translate('approvalRequest.toasts.approvalRequestDeclinedSuccess'));
                } else if (approvalRequest.state === APPROVAL_STATE.APPROVED) {
                    addToast(translate('approvalRequest.toasts.approvalRequestApprovedSuccess'));
                }
            })
            .catch(() => {
                dispatch({ type: TYPES.UPDATE_APPROVAL_REQUEST_ERROR });

                addErrorToast(translate('approvalRequest.toasts.approvalRequestUpdateError'));
            });
    };

    const onApprovalRequestApproved = (message?: string) => {
        const latestApprovalRequest = state.approvalRequests[0];

        updateApprovalRequest({
            approvalRequest: {
                ...latestApprovalRequest,
                state: APPROVAL_STATE.APPROVED,
                responseMessage: message || null,
            },
        });
    };

    const onApprovalRequestDeclined = (message?: string) => {
        const latestApprovalRequest = state.approvalRequests[0];

        updateApprovalRequest({
            approvalRequest: {
                ...latestApprovalRequest,
                state: APPROVAL_STATE.DECLINED,
                responseMessage: message || null,
            },
        });
    };

    if (
        state.fetchingData ||
        isDecrypting ||
        isLoadingToolToken ||
        (secret && !decryptedToken && !token && !toolToken)
    ) {
        return <LoadingWithWobble />;
    }

    return (
        <Box height="100vh" backgroundColor="background.default">
            <OfferApprovalRequestContent
                approvalRequests={state.approvalRequests}
                offer={state.offer}
                onApprovalRequestDeclined={onApprovalRequestDeclined}
                onApprovalRequestApproved={onApprovalRequestApproved}
                updatingApprovalRequestState={state.updatingApprovalRequestState || ApprovalRequestState.PENDING}
                error={state.error || ''}
                shouldDisplayBackButton={!token && !secret}
            />
        </Box>
    );
};
